import { observer } from "mobx-react";
import usePage from "../../../../../../components/layouts/base-page-layout/page-hook";
import { useCallback, useEffect, useRef, useState } from "react";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import TranslationScope, { ITranslationScopeContext } from "../../../../../../translation/translation-scope";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import { ConfirmModal } from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import ControlGroupWithPreview from "../../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import { FormField } from "../../../../../../components/form-field/form-field";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import { RETURNS_PORTAL_TRANSLATION_KEYS } from "../../constants";
import ExchangeProduct1Preview from '../../../../../../images/previews/product-exchange-1.svg';
import ExchangeProduct3Preview from '../../../../../../images/previews/product-exchange-3.svg';
import ExchangeProduct4Preview from '../../../../../../images/previews/product-exchange-4.svg';
import BxGy1Preview from '../../../../../../images/previews/bxgy-1.svg';
import BxGy2Preview from '../../../../../../images/previews/bxgy-2.svg';
import BxGy3Preview from '../../../../../../images/previews/bxgy-3.svg';
import ReturnPortalErros from "./return-portal-errors";
import TranslationTextarea from "../../../../../../translation/translation-textarea/translation-textarea";
import FontHeading4 from "../../../../../../components/fonts/font-heading4/font-heading4";
import { FONT_VARIANT } from "../../../../../../components/fonts/font-props";

export interface IReturnPortalErrorScope {
    hasChanges: ITranslationScopeContext['hasChanges'],
    save: ITranslationScopeContext['save'],
    discardChanges: ITranslationScopeContext['discardChanges'],
}

const ProductExchangeEditorPage = observer(() => {
    const { setSubTitles } = usePage();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    useEffect(() => {
        setSubTitles(['Portal', 'Content', 'Return Portal', 'Product Exchange']);
    }, [setSubTitles]);

    const _refReturnPortalErrorScope = useRef<IReturnPortalErrorScope>();
    const returnPortalErrorsScopeHasChanges = (): boolean => {
        if (_refReturnPortalErrorScope.current) {
            return _refReturnPortalErrorScope.current?.hasChanges();
        }
        return false;
    }
    const returnPortalErrorsScopeSave = (): void => {
        if (_refReturnPortalErrorScope.current) {
            _refReturnPortalErrorScope.current?.save();
        }
    }
    const returnPortalErrorsScopeDiscardChanges = (): void => {
        if (_refReturnPortalErrorScope.current) {
            _refReturnPortalErrorScope.current?.discardChanges();
        }
    }

    const setReturnPortalErrorsScopeMethods = useCallback((scope: IReturnPortalErrorScope) => {
        _refReturnPortalErrorScope.current = scope;
    }, []);

    const {
        hasChanges: hasTranslationChanges,
        discardChanges: translationsDiscardChanges,
        save: saveTranslations,
        selectedLocale
    } = useTranslation();

    const hasChanges = () => {
        return returnPortalErrorsScopeHasChanges() || hasTranslationChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        returnPortalErrorsScopeDiscardChanges();
        translationsDiscardChanges();
    }

    const onSaveClicked = () => {
        const promised = [];

        if (returnPortalErrorsScopeHasChanges()) {
            promised.push(
                returnPortalErrorsScopeSave()
            );
        }

        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return (
        <TranslationScope scope="assistant" locale={selectedLocale}>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        disabled: !hasChanges(),
                        onClick: onSaveClicked
                    }
                }}
            >
                <ListLayout>
                    <ControlGroupWithPreview hasTopBorder={false} preview={<img src={ExchangeProduct1Preview} alt="" />}>
                        <ListLayout>
                            <FormField label="Stage Header">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.EXCHANGE_PRODUCT_TITLE} />
                            </FormField>

                            <FormField label="Search Box - Hint">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.SEARCH} />
                            </FormField>

                            <FormField label="Search Instructions">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.SEARCH_INSTRUCTIONS} />
                            </FormField>

                            <FormField label="Search Instructions Emojis">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.SEARCH_INSTRUCTIONS_ICON} />
                            </FormField>

                            <FormField label="Browse Link Text">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.BROWSE} />
                            </FormField>

                            <FormField label="Browse Link URL">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.BROWSE_STORE_URL} />
                            </FormField>

                            <FormField label="Browse Link Instructions">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.OUR_STORE_FOR_IDEAS} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ExchangeProduct3Preview} alt="" />}>
                        <ListLayout>
                            <FormField label="No Additional Results">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.NO_ADDITIONAL_RESULTS} />
                            </FormField>

                            <FormField label="No Additional Results Emojis">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.NO_ADDITIONAL_RESULTS_ICON} />
                            </FormField>

                            <FormField label="Search Again">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.NO_ADDITIONAL_RESULTS_SEARCH_AGAIN} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ExchangeProduct4Preview} alt="" />}>
                        <ListLayout>
                            <FormField label="No Results">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.NO_RESULTS} />
                            </FormField>

                            <FormField label="No Results Emojis">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.NO_RESULTS_ICON} />
                            </FormField>

                            <FormField label="Try Again">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.NO_RESULTS_TRY_AGAIN} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <TranslationScope scope="return-portal-errors" locale={selectedLocale}>
                        <ReturnPortalErros onTranslationMethodsReady={setReturnPortalErrorsScopeMethods} />
                    </TranslationScope>

                    <ControlGroupWithPreview preview={<img src={BxGy1Preview} alt="" />}>
                        <ListLayout>
                            <FontHeading4 variant={FONT_VARIANT.BOLD}>BxGy</FontHeading4>

                            <FormField label="“Customer Buys” Header">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.BXGY_CUSTOMER_BUYS_HEADLINE} />
                            </FormField>

                            <FormField label="“Customer Gets” Header">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.BXGY_CUSTOMER_GETS_HEADLINE} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={BxGy2Preview} alt="" />}>
                        <ListLayout>
                            <FormField label="Note Title">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.NOTE_TITLE} />
                            </FormField>

                            <FormField label="Error message: Chosen Item Warning">
                                <TranslationTextarea translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.DISCOUNT_CHOSEN_ITEM_WARNING} />
                            </FormField>

                            <FormField label="Error message: Refund Warning">
                                <TranslationTextarea translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.DISCOUNT_REFUND_WARNING} />
                            </FormField>

                            <FormField label="Error message: Exchange Warning">
                                <TranslationTextarea translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.DISCOUNT_EXCHANGE_WARNING} />
                            </FormField>

                            <FormField label="Error message: Mixed Warning">
                                <TranslationTextarea translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.DISCOUNT_MIXED_WARNING} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={BxGy3Preview} alt="" />}>
                        <ListLayout>
                            <FormField label="Discount included tag">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.INCLUDED_IN_DISCOUNT} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>
                </ListLayout>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </TranslationScope>
    );
});

export default ProductExchangeEditorPage;